import React, { useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, CreditCard, ArrowLeft, Info } from "lucide-react";

export default function Profile() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Set page title and refresh user on mount
  useEffect(() => {
    if (user?.name) {
      const firstName = user.name.split(" ")[0];
      document.title = `${firstName}'s Profile on BacklinkBanana`;
    } else {
      document.title = "Profile | BacklinkBanana";
    }
    refreshUser();
  }, [refreshUser, user]);

  const handleTopUp = () => {
    // Navigate to your PurchaseCredits page
    navigate("/buy-credits");
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-100 to-white">
        <div className="bg-white rounded-xl shadow-lg px-8 py-8 text-center text-gray-600 font-semibold">
          Loading profile...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-100 to-white flex flex-col justify-between">
      {/* Profile Card */}
      <div className="flex flex-col items-center pt-28 pb-10">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full px-8 py-10">
          <h2 className="text-3xl font-bold mb-2 text-blue-800 text-center">
            {user.name.split(" ")[0]}'s Profile
          </h2>
          <p className="text-center text-gray-600 mb-6">
            Manage your account settings and credits.
          </p>

          {/* Info Block */}
          <div className="space-y-4 text-base sm:text-lg text-left text-gray-800 border-t border-gray-100 pt-4">
            <div>
              <span className="font-semibold text-gray-700">Name:</span>{" "}
              {user.name || "—"}
            </div>
            <div>
              <span className="font-semibold text-gray-700">Email:</span>{" "}
              {user.email || "—"}
            </div>
            <div>
              <span className="font-semibold text-gray-700">Credits:</span>{" "}
              <span
                className={
                  user.credits === 0
                    ? "text-red-600 font-bold"
                    : "text-green-700 font-bold"
                }
              >
                {user.credits}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-8 justify-center">
            <button
              onClick={handleTopUp}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow transition"
            >
              <CreditCard size={16} />
              Top Up Credits
            </button>

            <Link
              to="/credit-history"
              className="flex items-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-800 px-4 py-2 rounded-md text-sm font-medium shadow transition"
            >
              <Info size={16} />
              Credit History
            </Link>

            <Link
              to="/dashboard"
              className="flex items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm font-medium shadow transition"
            >
              <ArrowLeft size={16} />
              Dashboard
            </Link>

            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow transition"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mb-6 text-gray-500 text-xs text-center px-4">
        <div className="space-x-4 mb-2">
          <Link to="/privacy-policy" className="hover:underline">
            Privacy Policy
          </Link>
          <span>|</span>
          <Link to="/terms-and-conditions" className="hover:underline">
            Terms & Conditions
          </Link>
        </div>
        &copy; {new Date().getFullYear()} Backlink Banana
      </footer>
    </div>
  );
}
